import React, { createContext, useContext, useState, useEffect } from 'react';
    import supabase from '../supabase/supabase';

    const AuthContext = createContext();

    export const useAuth = () => {
      const context = useContext(AuthContext);
      if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
      }
      return context;
    };

    export const AuthProvider = ({ children }) => {
      const [user, setUser] = useState(null);
      const [profile, setProfile] = useState(null);
      const [loading, setLoading] = useState(true);

      useEffect(() => {
        // Check active sessions and sets the user
        supabase.auth.getSession().then(({ data: { session } }) => {
          setUser(session?.user ?? null);
          if (session?.user) fetchProfile(session.user.id);
          else setLoading(false);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
          setUser(session?.user ?? null);
          if (session?.user) {
            fetchProfile(session.user.id);
          } else {
            setProfile(null);
            setLoading(false);
          }
        });

        return () => subscription.unsubscribe();
      }, []);

      const fetchProfile = async (userId) => {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

          if (!error && data) {
            setProfile(data);
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
        } finally {
          setLoading(false);
        }
      };

      const signInWithMagicLink = async (email) => {
        const { data, error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: window.location.origin + '/dashboard',
          },
        });
        if (error) throw error;
        return data;
      };

      const logout = async () => {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
        setUser(null);
        setProfile(null);
      };

      const updateProfile = async (userId, updates) => {
        try {
          const { error } = await supabase
            .from('profiles')
            .update(updates)
            .eq('id', userId);

          if (error) throw error;
          
          // Only update local profile state if it's the current user
          if (user && user.id === userId) {
            setProfile(prev => ({ ...prev, ...updates }));
          }
          
          return { success: true };
        } catch (error) {
          console.error('Error updating profile:', error);
          return { success: false, error };
        }
      };

      // --- Admin User Management Functions ---

      const getAllUsers = async () => {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
      };

      const toggleUserSuspension = async (userId, currentStatus) => {
        const { error } = await supabase
          .from('profiles')
          .update({ suspended: !currentStatus })
          .eq('id', userId);
        if (error) throw error;
      };

      const updateUserBadges = async (userId, badges) => {
        const { error } = await supabase
          .from('profiles')
          .update({ badges })
          .eq('id', userId);
        if (error) throw error;
      };

      const updateUserMembership = async (userId, status) => {
        const { error } = await supabase
          .from('profiles')
          .update({ membership_status: status })
          .eq('id', userId);
        if (error) throw error;
      };

      const deleteUser = async (userId) => {
        const { error } = await supabase
          .from('profiles')
          .delete()
          .eq('id', userId);
        if (error) throw error;
      };

      const value = {
        user,
        role: profile?.role || 'guest', // 'admin', 'user', 'guest'
        profile,
        signInWithMagicLink,
        logout,
        loading,
        updateProfile,
        getAllUsers,
        toggleUserSuspension,
        updateUserBadges,
        updateUserMembership,
        deleteUser
      };

      return (
        <AuthContext.Provider value={value}>
          {!loading && children}
        </AuthContext.Provider>
      );
    };